import React, { useEffect, useRef, useState } from "react";
import { number, string } from "yup";
import RatingNumber from "../RatingNumber";
import {
  Armchair,
  CirclePlus,
  CircleCheck,
  Divide,
  Fuel,
  MessageSquare,
  PackageOpen,
  Palette,
  Shield,
  ShieldCheck,
  Smile,
  Star,
  ThumbsDown,
  ThumbsUp,
  Trophy,
  Wrench,
} from "lucide-react";
import { Button, Input, Progress, Textarea } from "../ui";
import { schema } from "./utils";
import { trackEvent } from "../../utils/analytics";
import { trackMetaEvent } from "../../utils/metaPixel";

type QualityFormData = {
  security_note: number;
  comfort_note: number;
  capacity_note: number;
  quality_note: number;
  consumption_note: number;
  maintenance_cost_note: number;
  fun_note: number;
  equipment_note: number;
  reliability_note: number;
  design_note: number;
  comment: string;
  tops: string;
  flops: string;
  email?: string;
};

type RatingQuestionsStepProps = {
  review: Partial<QualityFormData>;
  onSubmit: (data: QualityFormData) => void;
  onPrevious: () => void;
};

type QuestionType = {
  label?: string;
  type?: "text" | "number" | "email";
  name: keyof QualityFormData;
  icon?: React.ReactNode;
  description: string;
  descriptionIcon?: React.ReactNode;
  validation?: any;
  divider?: boolean;
  lockedBy?: keyof QualityFormData;
};

const questions: QuestionType[] = [
  {
    label: "Sécurité",
    name: "security_note",
    type: "number",
    icon: <Shield className="size-4" strokeWidth={3} />,
    description: "Vous sentez-vous en sécurité dans votre véhicule ?",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
  },
  {
    label: "Confort",
    name: "comfort_note",
    type: "number",
    description: "Quel est le niveau de confort de votre véhicule ?",
    icon: <Armchair className="size-4" strokeWidth={3} />,
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "security_note",
  },
  {
    label: "Habitabilité",
    name: "capacity_note",
    type: "number",
    icon: <PackageOpen className="size-4" strokeWidth={3} />,
    description:
      "Que pensez-vous des espaces de coffres, espace de vie, rangements ?",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "comfort_note",
  },
  {
    label: "Qualité",
    name: "quality_note",
    type: "number",
    icon: <Star className="size-4" strokeWidth={3} />,
    description:
      "Que pensez-vous de la qualité des finitions de votre véhicule ?",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "capacity_note",
  },
  {
    label: "Consommation",
    name: "consumption_note",
    type: "number",
    icon: <Fuel className="size-4" strokeWidth={3} />,
    description:
      "Que pensez-vous de la consommation moyenne de votre véhicule ? 10 signifiant une faible consommation",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "quality_note",
  },
  {
    label: "Coût de maintenance",
    name: "maintenance_cost_note",
    type: "number",
    icon: <Wrench className="size-4" strokeWidth={3} />,
    description:
      "Que pensez-vous du coût de maintenance et d'entretien de votre véhicule ? 10 signifiant un faible coût de maintenance",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "consumption_note",
  },
  {
    label: "Plaisir de conduite",
    name: "fun_note",
    type: "number",
    icon: <Smile className="size-4" strokeWidth={3} />,
    description:
      "Quel est votre niveau de plaisir de conduite de votre véhicule ?",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "maintenance_cost_note",
  },
  {
    label: "Équipement",
    name: "equipment_note",
    type: "number",
    icon: <CirclePlus className="size-4" strokeWidth={3} />,
    description: "Que pensez-vous du niveau d'équipement de votre véhicule ?",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "fun_note",
  },
  {
    label: "Fiabilité",
    name: "reliability_note",
    type: "number",
    icon: <ShieldCheck className="size-4" strokeWidth={3} />,
    description: "Que pensez-vous de la fiabilité de votre véhicule ?",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "equipment_note",
  },
  {
    label: "Design",
    name: "design_note",
    type: "number",
    icon: <Palette className="size-4" strokeWidth={3} />,
    description: "Que pensez-vous du design de votre véhicule ?",
    validation: number()
      .required("La note est requise")
      .min(1, "La note doit être entre 1 et 10")
      .max(10, "La note doit être entre 1 et 10"),
    divider: true,
    lockedBy: "reliability_note",
  },
  {
    label: "Afin de poursuivre...",
    description: "Quelles sont les avantages de votre véhicule ?",
    descriptionIcon: (
      <ThumbsUp className="size-4 text-green-500" strokeWidth={2} />
    ),
    name: "tops",
    type: "text",
    icon: <MessageSquare className="size-4" strokeWidth={3} />,
    divider: false,
    lockedBy: "design_note",
  },
  {
    description: "Quelles sont les inconvénients de votre véhicule ?",
    descriptionIcon: (
      <ThumbsDown className="size-4 text-red-500" strokeWidth={2} />
    ),
    name: "flops",
    type: "text",
    divider: true,
    lockedBy: "design_note",
  },
  {
    label: "Tentez de gagner un cadeau",
    description:
      "Si vous souhaitez participer à notre jeu concours, saisissez votre adresse email ",
    name: "email",
    type: "email",
    icon: <Trophy className="size-4" strokeWidth={3} />,
    validation: string().email("L'email est invalide"),
    divider: false,
  },
];

const RatingQuestionsStep: React.FC<RatingQuestionsStepProps> = (props) => {
  const [review, setReview] = useState<QualityFormData>(
    (props.review || {}) as QualityFormData,
  );
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dividerRefs = useRef<Record<string, HTMLHRElement | null>>({});

  const ratingQuestions = questions.filter((q) => q.type === "number");
  const answeredCount = ratingQuestions.filter(
    (q) => (review[q.name] as number) >= 1,
  ).length;
  const progressPercentage = Math.round(
    (answeredCount / ratingQuestions.length) * 100,
  );
  const allRatingsValid = ratingQuestions.every((q) =>
    q.validation?.isValidSync(review[q.name]),
  );

  useEffect(() => {
    trackEvent({
      name: "begin_checkout",
      params: {
        event_name: "form_start",
        page_type: "review_form",
        step: "form_start",
      },
    });
    trackMetaEvent(
      "Lead",
      {
        content_ids: ["review_form"],
        content_type: "product",
        page_type: "Formulaire évaluation",
        step: "form_start",
      },
      {
        eventID: "form_start",
      },
    );
  }, []);

  const onSubmit = () => {
    schema.validate(review).then((data) => {
      props.onSubmit({
        security_note: data.security_note,
        comfort_note: data.comfort_note,
        capacity_note: data.capacity_note,
        quality_note: data.quality_note,
        consumption_note: data.consumption_note,
        maintenance_cost_note: data.maintenance_cost_note,
        fun_note: data.fun_note,
        equipment_note: data.equipment_note,
        reliability_note: data.reliability_note,
        design_note: data.design_note,
        comment: data.comment,
        tops: data.tops,
        flops: data.flops,
        email: data.email,
      });
    });
  };

  const onChange = (name: keyof QualityFormData, value: number | string) => {
    const wasUnanswered = !((review[name] as number) >= 1);

    setReview((old) => ({ ...old, [name]: value }));

    const question = questions.find((q) => q.name === name);
    if (wasUnanswered && question?.type === "number") {
      setTimeout(() => {
        dividerRefs.current[name]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-6">
        <Progress value={progressPercentage} className="flex-grow-1" />
        <span className="text-sm text-gray-500 whitespace-nowrap">
          {answeredCount} / {ratingQuestions.length}
        </span>
      </div>

      <div className="flex flex-col gap-6">
        {questions.map((question) => {
          const isLocked =
            !!question.lockedBy &&
            !((review[question.lockedBy] as number) >= 1);

          return (
            <React.Fragment key={question.name}>
              <div
                ref={(el) => {
                  questionRefs.current[question.name] = el;
                }}
              >
                <Question
                  question={question}
                  value={review[question.name]}
                  onChange={onChange}
                  isLocked={isLocked}
                />
              </div>
              {question.divider && (
                <hr
                  ref={(el) => {
                    dividerRefs.current[question.name] = el;
                  }}
                  className="border-gray-200"
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <Button
        variant="default"
        className="mt-8 w-full"
        disabled={!allRatingsValid}
        onClick={onSubmit}
      >
        Envoyer mon avis
      </Button>
    </>
  );
};

const Question = (props: {
  question: QuestionType;
  value: number | string | undefined;
  onChange: (name: keyof QualityFormData, value: number | string) => void;
  isLocked?: boolean;
}) => {
  const { question } = props;
  const isAnswered =
    question.type === "number"
      ? (props.value as number) >= 1
      : !!(props.value as string)?.trim();
  return (
    <div className="relative">
      {props.isLocked && (
        <div className="absolute inset-[-4px] z-10 rounded-lg bg-white/60 cursor-not-allowed" />
      )}
      {question.label && (
        <p className="text-xl font-bold flex items-center gap-2">
          {isAnswered ? (
            <CircleCheck className="size-4 text-green-500" strokeWidth={2.5} />
          ) : (
            question.icon
          )}
          {question.label}
        </p>
      )}
      <p
        className={`mb-4 text-sm text-gray-500 flex items-center gap-1.5${question.descriptionIcon ? " mt-1" : ""}`}
      >
        {question.descriptionIcon}
        {question.description}
      </p>
      {question.type === "number" && (
        <RatingNumber
          minRating={1}
          maxRating={10}
          value={props.value as number}
          onChange={(v) => props.onChange(question.name, v)}
        />
      )}
      {question.type === "text" && (
        <Textarea
          className="mt-4"
          value={(props.value as string) || ""}
          onChange={(e) => props.onChange(question.name, e.target.value)}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = "0px";
            target.style.height = target.scrollHeight + "px";
          }}
        />
      )}
      {question.type === "email" && (
        <div className="mt-4 space-y-2">
          <label
            htmlFor="email"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Email
          </label>
          <Input
            id="email"
            type="email"
            placeholder="jean@exemple.com"
            value={(props.value as string) || ""}
            onChange={(e) => props.onChange(question.name, e.target.value)}
          />
        </div>
      )}
    </div>
  );
};

export default RatingQuestionsStep;
